import AWS from "aws-sdk";
import {
    getActiveIdentityPoolId,
    getActiveIdentityPoolRegion,
    getOperationalConfig
} from "./network/BaseURLUtils";
import {ensureOperationalIdToken, getOperationalIdTokenSync} from "./OperationalAuth";

/**
 * Builds the Cognito `Logins` map that turns the operational identity pool call
 * into an *authenticated* GetCredentialsForIdentity request instead of a guest
 * (unauthenticated) one (Shepherd eaa16814).
 *
 * The token is minted against the NEW, self-contained operational user pool by
 * OperationalAuth, which mirrors the katal-logger path using this account's own
 * resources: read the dummy creds from S3 via a guest credentials identity pool
 * -> initiateAuth against the operational user pool -> IdToken. This is NOT the
 * katal-logger token; the OPERATIONAL identity pool itself stays no-guest.
 *
 * Returns `undefined` when the operational pool is not configured yet
 * (pre-cutover) or no token is available, so getS3Client() falls back to the
 * existing pool unchanged.
 */
const buildLogins = () => {
    const operationalConfig = getOperationalConfig();
    if (!operationalConfig) {
        return undefined; // not cut over yet; use existing pool without Logins
    }
    const idToken = getOperationalIdTokenSync();
    if (!idToken) {
        return undefined;
    }
    const providerName =
        `cognito-idp.${operationalConfig.region}.amazonaws.com/${operationalConfig.userPoolId}`;
    return {
        [providerName]: idToken
    };
}

/**
 * Proactively mint/refresh the operational IdToken. Should be awaited by the
 * app's periodic sync loop (and on startup) so getS3Client() has a fresh token
 * to place in the Logins map. Safe no-op before cutover.
 */
const refreshS3Auth = async () => {
    return ensureOperationalIdToken();
}

const getS3Client = () => {
    const logins = buildLogins();

    const cognitoConfig = {
        IdentityPoolId: getActiveIdentityPoolId()
    };

    // Only attach Logins when we have an operational token. Attaching an
    // empty/undefined Logins map would still request guest credentials, so we
    // keep the config free of it in that case rather than sending junk.
    if (logins) {
        cognitoConfig.Logins = logins;
    }

    AWS.config.update({
        region: getActiveIdentityPoolRegion(),
        credentials: new AWS.CognitoIdentityCredentials(cognitoConfig),
        maxRetries: 3
    });

    return new AWS.S3();
}

export {getS3Client, refreshS3Auth}
